//==============================================================================
// Account Lookup
//
// Allows users to look up if a corresponding store account already exists with
// their information, adds entered email to the account, and redirects user to
// the B2C sign up page.
//==============================================================================
import * as React from 'react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import classnames from 'classnames';

import { clsHelper } from '../../utilities/class-name-helper';
import { varReplace } from '../../utilities/variable-replacer';

import { ILinkData, RichTextComponent, RichText } from '@msdyn365-commerce/core';
import { Button, Heading, IHeadingProps } from '@msdyn365-commerce-modules/utilities';

import { IAccountLookupData } from './account-lookup.data';
import { IAccountLookupProps } from './account-lookup.props.autogenerated';
import { CustomerMatchInput, customerMatchActionDataAction } from './actions/customer-match';
import { SetCustomerMatchEmailInput, setCustomerMatchEmailActionDataAction } from './actions/set-customer-match-email';
import { ICustomerMatchCriteria, ICustomerMatchFields, ICustomResponse_1OfCustomerMatchResult, ICustomerMatchEmailInformation } from '../../actions/DataServiceEntities.g';

//==============================================================================
// INTERFACES
//==============================================================================
export interface FormField {
    id: string;
    label: string;
    fieldType: string;
    validation?: string;
    error?: string;
    link?: FormLinkData;
}

export interface FormLinkData {
    linkText?: string;
    linkUrl: ILinkData;
    ariaLabel?: string;
    openInNewTab?: boolean;
}

export interface FormData {
    [key: string]: string;
}

//==============================================================================
// CLASS NAME UTILITY
//==============================================================================
const BASE_CLASS = 'account-lookup';
const cls = (fragment?: string) => clsHelper(BASE_CLASS, fragment);

//==============================================================================
// CLASS DEFINITION
//==============================================================================
/**
 * AccountLookup component
 * @extends {React.PureComponent<IAccountLookupProps<IAccountLookupData>>}
 */
//==============================================================================
@observer
class AccountLookup extends React.PureComponent<IAccountLookupProps<IAccountLookupData>> {
    //==========================================================================
    // VARIABLES
    //==========================================================================
    @observable private formData: FormData = {
        phone: '',
        name: '',
        email: '',
        card: '',
        zip: ''
    };
    @observable private emailData: FormData = {
        email: ''
    };
    private formFields: FormField[] = [
        {
            id: 'phone',
            label: this.props.resources.accountLookup_phone,
            fieldType: 'text',
            validation: '^(\\+\\d{1,2}\\s?)?\\(?\\d{3}\\)?[\\s.-]?\\d{3}[\\s.-]?\\d{4}$',
            error: this.props.resources.accountLookup_errorInvalidPhone
        },
        {
            id: 'name',
            label: this.props.resources.accountLookup_name,
            fieldType: 'text'
        },
        {
            id: 'email',
            label: this.props.resources.accountLookup_email,
            fieldType: 'email',
            validation: '.+\@.+\..+',
            error: this.props.resources.accountLookup_errorInvalidEmail
        },
        {
            id: 'card',
            label: this.props.resources.accountLookup_card,
            fieldType: 'text',
            link: this.props.config.linkCardNumber,
            validation: '^[0-9]{8,12}$',
            error: this.props.resources.accountLookup_errorInvalidCard
        },
        {
            id: 'zip',
            label: this.props.resources.accountLookup_zip,
            fieldType: 'text',
            validation: '^[0-9]{5}(?:-[0-9]{4})?$',
            error: this.props.resources.accountLookup_errorInvalidZip
        }
    ];
    @observable private errorMessages: string[] = [];
    @observable private result?: ICustomResponse_1OfCustomerMatchResult;
    private resultStatusTable: { [key: string]: string } = {
        match: 'MATCH',
        noMatch: 'NO_MATCH',
        ambiguous: 'AMBIGUOUS',
        error: 'ERROR'
    };
    private resultErrorTable: { [key: string]: string } = {
        INSUFFICIENT_PARAMS: this.props.resources.accountLookup_errorInsufficientParameters,
        INVALID_EMAIL: this.props.resources.accountLookup_errorInvalidEmail,
        INVALID_CARD: this.props.resources.accountLookup_errorInvalidCard,
        INVALID_PHONE: this.props.resources.accountLookup_errorInvalidPhone,
        INVALID_ZIP: this.props.resources.accountLookup_errorInvalidZip,
        INVALID_CONFIG: this.props.resources.accountLookup_errorGeneral,
        JWT_SIGN_FAILURE: this.props.resources.accountLookup_errorGeneral,
        SERVER_ERROR: this.props.resources.accountLookup_errorGeneral
    };
    private signUpUrl: string = this.props.context.request.user.signUpUrl || '';
    private signInUrl: string = this.props.context.request.user.signInUrl || '';

    //==========================================================================
    // PUBLIC METHODS
    //==========================================================================
    //------------------------------------------------------
    // Render function
    //------------------------------------------------------
    public render(): JSX.Element | null {
        const { config } = this.props;

        const resultMap: { [key: string]: boolean } = {
            form: !this.result || (this.result.Status === this.resultStatusTable.error),    // Show form if there is no result OR if result is an error
            match: this.result?.Status === this.resultStatusTable.match,
            noMatch: this.result?.Status === this.resultStatusTable.noMatch,
            ambiguous: this.result?.Status === this.resultStatusTable.ambiguous
        };

        return (
            <div className={classnames(BASE_CLASS, config.className)}>
                <div className={cls('header')}>
                    {config.heading && this._renderHeading(config.heading, 'heading-title')}
                    {resultMap.form && this._renderTabHeader(config.headingForm, config.textForm)}
                    {resultMap.match && this._renderTabHeader(config.headingMatch, config.textMatch)}
                    {resultMap.noMatch && this._renderTabHeader(config.headingNoMatch, config.textNoMatch)}
                    {resultMap.ambiguous && this._renderTabHeader(config.headingAmbiguous, config.textAmbiguous)}
                </div>

                <div className={cls('body')}>
                    {resultMap.form && this._renderForm()}
                    {resultMap.match && this._renderMatch(resultMap)}
                    {(resultMap.noMatch || resultMap.ambiguous) && this._renderMatchFailure(resultMap)}
                </div>
            </div>
        );
    }

    //==========================================================================
    // PRIVATE METHODS
    //==========================================================================
    //------------------------------------------------------
    // Render heading
    //------------------------------------------------------
    private readonly _renderHeading = (heading: IHeadingProps, className?: string): JSX.Element => {
        return (
            <Heading
                className={classnames(cls('heading'), cls(className))}
                headingTag={heading.tag}
                text={heading.text}
            />
        );
    };

    //------------------------------------------------------
    // Render rich text
    //------------------------------------------------------
    private readonly _renderRichText = (text: RichText, className?: string): JSX.Element => {
        return (
            <RichTextComponent
                className={classnames(cls('text'), cls(className))}
                text={text}
            />
        );
    };

    //------------------------------------------------------
    // Render tab header section
    //------------------------------------------------------
    private readonly _renderTabHeader = (heading?: IHeadingProps, text?: RichText): JSX.Element => {
        return (
            <>
                {heading && this._renderHeading(heading, 'heading-subtitle')}
                {text && this._renderRichText(text)}
            </>
        );
    };

    //------------------------------------------------------
    // Render email messaging
    //------------------------------------------------------
    private readonly _renderEmailMessage = (text: RichText, email: string): JSX.Element => {
        return (
            <RichTextComponent
                className={classnames(cls('text'), cls('email-message'))}
                text={varReplace(text as string, { email: email })}
            />
        );
    };

    //------------------------------------------------------
    // Render customer lookup form
    //------------------------------------------------------
    private readonly _renderForm = (): JSX.Element => {
        const { resources } = this.props;

        return (
            <>
                <div className={cls('form')}>
                    {this.formFields.map((field: FormField, index: number) => (
                        <React.Fragment key={index}>
                            {this._renderInputGroup(field, this.formData)}
                        </React.Fragment>
                    ))}
                </div>
                {!!this.errorMessages.length && this._renderErrors(this.errorMessages)}
                {this._renderMatchError()}
                <div className={cls('buttons')}>
                    <Button className={classnames(cls('button'), cls('button-primary'))} onClick={this._submitForm}>
                        {resources.accountLookup_buttonFind}
                    </Button>

                    <a className={classnames(cls('link'))} href={this.signInUrl} aria-label={resources.accountLookup_buttonSignIn}>
                        {resources.accountLookup_buttonSignIn}
                    </a>
                </div>
            </>
        );
    };

    //------------------------------------------------------
    // Render customer lookup match
    //------------------------------------------------------
    private readonly _renderMatch = (resultMap: { [key: string]: boolean }): JSX.Element => {
        const { config, resources } = this.props;
        const emailField = this.formFields.find(field => field.id === 'email');
        const formEmail = this.formData.email;
        const custMatchInfo = this.result?.Data?.CustomerMatchInformation;
        const custEmail = custMatchInfo?.Email;

        return (
            <>
                {/* If email was not supplied & cust record does not have an email, prompt for email with input field */}
                {!formEmail && !custEmail && !!emailField &&
                    <>
                        {!!config.textMatchEmail && this._renderRichText(config.textMatchEmail, 'form-heading')}
                        <div className={cls('form')}>
                            {this._renderInputGroup(emailField, this.emailData)}
                        </div>
                    </>
                }

                {/* If email was not supplied & cust record has an email, show messaging with email address */}
                {!formEmail && !!custEmail && !!config.textMatchEmailExisting && this._renderEmailMessage(config.textMatchEmailExisting, custEmail)}

                {/* If email was supplied & matches cust record email, show messaging with email address */}
                {!!formEmail && !!custEmail && (formEmail === custEmail) && !!config.textMatchEmailMatch && this._renderEmailMessage(config.textMatchEmailMatch, custEmail)}

                {/* If email was supplied & cust record has a different email, show messaging with email address */}
                {!!formEmail && !!custEmail && (formEmail !== custEmail) && !!config.textMatchEmailMismatch && this._renderEmailMessage(config.textMatchEmailMismatch, custEmail)}

                {!!this.errorMessages.length && this._renderErrors(this.errorMessages)}
                <div className={cls('buttons')}>
                    <Button className={classnames(cls('button'), cls('button-primary'))} onClick={() => this._createAccount(resultMap.match)}>
                        {resources.accountLookup_buttonMatchSignUp}
                    </Button>
                    <Button className={classnames(cls('button'), cls('button-secondary'))} onClick={this._resetResult}>
                        {resources.accountLookup_buttonBack}
                    </Button>
                </div>
            </>
        );
    };

    //------------------------------------------------------
    // Render customer lookup failure
    //------------------------------------------------------
    private readonly _renderMatchFailure = (resultMap: { [key: string]: boolean }): JSX.Element => {
        const { config, resources } = this.props;

        return (
            <>
                <div className={cls('buttons')}>
                    <Button className={classnames(cls('button'), cls('button-primary'))} onClick={this._resetResult}>
                        {resources.accountLookup_buttonReset}
                    </Button>
                    {config.linkContact &&
                        <a
                            className={classnames(cls('button'), cls('button-secondary'))}
                            href={config.linkContact.linkUrl.destinationUrl}
                            aria-label={config.linkContact.ariaLabel}
                            target={config.linkContact.openInNewTab ? '_blank' : undefined}
                            rel='noreferrer'
                        >{config.linkContact.linkText}</a>
                    }
                </div>
            </>
        );
    };

    //------------------------------------------------------
    // Render input group
    //------------------------------------------------------
    private readonly _renderInputGroup = (input: FormField, formData: FormData): JSX.Element => {
        return (
            <div className={classnames(cls('input-group'), cls(`input-${input.id}`))}>
                {input.link &&
                    <a
                        className={cls('label-link')}
                        href={input.link.linkUrl.destinationUrl}
                        aria-label={input.link.ariaLabel}
                        target={input.link.openInNewTab ? '_blank' : undefined}
                        rel='noreferrer'
                    >{input.link.linkText}</a>
                }
                <label
                    className={cls('label')}
                    htmlFor={input.id}
                >
                    {input.label}
                </label>
                <input
                    className={cls('input')}
                    type={input.fieldType}
                    onChange={(e) => this._onInputChange(e, formData)}
                    id={input.id}
                    value={formData[input.id]}
                />
            </div>
        );
    };

    //------------------------------------------------------
    // On input change
    //------------------------------------------------------
    private readonly _onInputChange = (event: React.ChangeEvent<HTMLInputElement>, formData: FormData): void => {
        // Only reset result on input change after a match error
        if (this.result?.Status === this.resultStatusTable.error) {
            this._resetResult();
        }
        formData[event.target.id] = event.target.value;
    };

    //------------------------------------------------------
    // Submit form
    //------------------------------------------------------
    private readonly _submitForm = (): void => {
        this.errorMessages = [];
        const errors = this._validateFields(this.formFields, this.formData);
        if (errors.length) {
            this.errorMessages = errors;
            return;
        }

        this._customerMatch();
    };

    //------------------------------------------------------
    // Customer match
    //------------------------------------------------------
    private readonly _customerMatch = async (): Promise<void> => {
        const { context, resources } = this.props;

        const customerCriteria: ICustomerMatchFields = {
            Name: this.formData.name,
            Email: this.formData.email,
            Card: this.formData.card,
            Phone: this.formData.phone,
            Zip: this.formData.zip,
        };

        const customerMatchCriteria: ICustomerMatchCriteria = {
            CustomerCriteria: customerCriteria
        };

        const input = new CustomerMatchInput(customerMatchCriteria);

        customerMatchActionDataAction(input, context.actionContext).then(result => {
            this.result = result;
        }).catch(error => {
            context.telemetry.error(error.message);

            // If call is failing, show general error
            this.errorMessages = [resources.accountLookup_errorGeneral];
        });
    };

    //------------------------------------------------------
    // Validate fields
    //------------------------------------------------------
    private readonly _validateFields = (fields: FormField[], values: FormData | { [key: string]: string }): string[] => {
        const validationErrors: string[] = [];

        fields.forEach(field => {
            const value = values[field.id];
            if (value && field.validation && field.error) {
                !this._validateField(field.validation, value) && validationErrors.push(field.error);
            }
        });
        return validationErrors;
    };

    //------------------------------------------------------
    // Validate field
    //------------------------------------------------------
    private readonly _validateField = (validation: string, value: string): boolean => {
        // eslint-disable-next-line security/detect-non-literal-regexp
        const regex = new RegExp(validation);
        return regex.test(value);
    };

    //------------------------------------------------------
    // Create account
    //------------------------------------------------------
    private readonly _createAccount = (showMatch: boolean): void => {
        if (showMatch && !this.result?.Data?.CustomerMatchInformation?.Email) {
            // If there was a match AND there is no email on the customer record

            if (this.formData.email) {
                // If email was entered and validated in the initial form, add email and redirect
                this._addEmail();
            } else if (this.emailData.email) {
                // If email was entered after a match, need to validate first, then add email and redirect
                const emailField = this.formFields.find(field => field.id === 'email');
                this.errorMessages = [];
                const errors = this._validateFields([emailField!], this.emailData);
                if (errors.length) {
                    this.errorMessages = errors;
                    return;
                }
                this._addEmail();
            } else {
                // If email was not entered at all, show error and do not redirect
                this.errorMessages = [this.props.resources.accountLookup_errorEmptyEmail];
                return;
            }
        } else {
            // If there was no match OR there is already an email on the customer record, then redirect
            this._redirectSignUp();
        }
    };

    //------------------------------------------------------
    // Redirect to signup page
    //------------------------------------------------------
    private readonly _redirectSignUp = (): void => {
        window.location.href = this._getRedirectUrl(this.signUpUrl);
    };

    //------------------------------------------------------
    // Get redirect url after creating new account
    //------------------------------------------------------
    private readonly _getRedirectUrl = (signUpUrl: string): string => {
        const { config } = this.props;
        const redirectUrl = config.linkRedirect;
        return redirectUrl ? `${signUpUrl}?ru=${redirectUrl}` : signUpUrl;
    };

    //------------------------------------------------------
    // Add email to customer record
    //------------------------------------------------------
    private readonly _addEmail = async (): Promise<void> => {
        const { context } = this.props;

        const setEmailInfo: ICustomerMatchEmailInformation = {
            MatchToken: this.result?.Data?.MatchToken,
            Email: this.emailData.email || this.formData.email
        };

        const input = new SetCustomerMatchEmailInput(setEmailInfo);

        setCustomerMatchEmailActionDataAction(input, context.actionContext).then(result => {
            if (result.Data) {
                // If setting email was successful, redirect to signup page
                this._redirectSignUp();
            } else {
                // If setting email was unsuccessful, show error and do not redirect
                this.errorMessages = [this.props.resources.accountLookup_failureEmail];
                return;
            }
        }).catch(error => {
            this.errorMessages = [this.props.resources.accountLookup_failureEmail];
            context.telemetry.error(error.message);
        });
    };

    //------------------------------------------------------
    // Render match error message
    //------------------------------------------------------
    private readonly _renderMatchError = (): JSX.Element | null => {
        const result = this.result;
        const status = result?.Status;
        const code = result?.Code;

        if (status === this.resultStatusTable.error) {
            const error = code && this.resultErrorTable[code];

            if (error) {
                return this._renderErrors([error]);
            }
        }
        return null;
    };

    //------------------------------------------------------
    // Render error messages
    //------------------------------------------------------
    private readonly _renderErrors = (errors: string[]): JSX.Element => {
        return (
            <div className={classnames(cls('alert'), 'error')}>
                {errors.map((error, index) => {
                    return (
                        <span key={index} className={cls('alert-message')}>
                            {error}
                        </span>
                    );
                })}
            </div>
        );
    };

    //------------------------------------------------------
    // Reset lookup result
    //------------------------------------------------------
    private readonly _resetResult = (): void => {
        this.result = undefined;
    };
}

export default AccountLookup;
